import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Download } from "lucide-react";
import { Alert, Skeleton, Typography } from "@akuna/ui";
import { useStayPhotosView } from "./useStayPhotosView";

const dayLabel = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * Página pública da estadia. Não mostra nada do responsável — só o nome do
 * pet, o período e as mídias.
 */
export default function StayPhotos() {
  const { t } = useTranslation("daycareHotel");
  const { token } = useParams<{ token: string }>();
  const view = useStayPhotosView(token);

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-3xl">
        {view.isLoading && <Skeleton className="h-64 rounded-3xl" label={t("common.loading")} />}

        {view.isError && (
          <Alert variant="danger">
            <Typography variant="h5">{t("share.invalidTitle")}</Typography>
            <Typography variant="body-sm">{t("share.invalidDescription")}</Typography>
          </Alert>
        )}

        {view.data && (
          <>
            <header className="mb-8 flex flex-col items-center gap-2 text-center">
              <img src="/assets/images/logo.svg" alt="Akuna" className="h-10 w-auto" />
              <Typography variant="h2">{view.data.petName}</Typography>
              <Typography variant="body-sm" className="text-muted-foreground">{view.data.periodLabel}</Typography>
              <Typography variant="body-sm" className="text-muted-foreground">{t("share.subtitle")}</Typography>
            </header>

            {view.data.media.length === 0 ? (
              <Typography variant="body-sm" className="text-center text-muted-foreground">{t("share.empty")}</Typography>
            ) : (
              <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {view.data.media.map((item) => (
                  <li key={item.id} className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
                    {item.mediaKind === "image" ? (
                      <img src={item.url} alt={item.description} className="aspect-[4/3] w-full object-cover" />
                    ) : (
                      <video src={item.url} controls className="aspect-[4/3] w-full object-cover" />
                    )}
                    <div className="flex items-start justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <Typography variant="body-sm" className="font-semibold text-foreground">{item.description}</Typography>
                        <Typography variant="caption" className="text-muted-foreground">
                          {dayLabel(item.day)} · {item.time}
                        </Typography>
                      </div>
                      <a
                        href={item.url}
                        download
                        className="flex shrink-0 items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-akuna-orange"
                      >
                        <Download aria-hidden="true" size={15} />
                        {t("share.download")}
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </main>
  );
}
